<script setup lang="ts">
/**
 * 问题升级 / 降级弹窗——问题清单和升级问题看板共用这一个（0923 从 ProjectIssueList 里抽出来）。
 *
 * 为什么抽：升级页要「在这一页里就能办」（Tony 0923），而弹窗原来长在问题清单里面。
 * 两处各留一份的话，「能选哪几级、原因必填、通知谁」迟早各说各的。
 *
 * 能不能升、降到哪、各级由谁接，全由后端 targets 接口算好，这里只照着亮。
 */
import { onMounted, ref } from 'vue';

import { eqsApi, type IssueEscalationTargets } from '@/api/eqs';
import { notify } from '@/services/notice';
import type { ModuleRecord } from '@/types/domain';
import { errorMessage, formatDate } from '@/utils/format';
import { escalationSourceLabel, issueLevelLabel, issueLevelTone } from '@/utils/issue-escalation';

const props = withDefaults(defineProps<{
  issue: { id: number | string; code?: string | null; title: string };
  /** 「建议升级」的理由（逾期 N 天 / 催过 N 次）；没有就不显示 */
  advice?: string | null;
}>(), { advice: null });

const emit = defineEmits<{
  close: [];
  /** 已写库；带回最新的问题记录，调用方就地替换 */
  escalated: [saved: ModuleRecord];
}>();

const info = ref<IssueEscalationTargets | null>(null);
const picked = ref(0);
const reason = ref('');
const busy = ref(true);
const error = ref('');

onMounted(async () => {
  try {
    const result = await eqsApi.issueEscalationTargets(props.issue.id);
    info.value = result;
    // 默认选「再上一级」——升级是常态，降级是例外
    picked.value = Math.min(4, result.currentLevel + 1);
  } catch (caught) {
    error.value = errorMessage(caught);
  } finally {
    busy.value = false;
  }
});

/** 能选的层级：升只能往上，降只能到地板 */
function selectable(level: number): boolean {
  const current = info.value;
  if (!current || level === current.currentLevel) return false;
  if (level > current.currentLevel) return current.canRaise;
  return current.canLower && level >= current.floorLevel;
}

async function submit(): Promise<void> {
  const current = info.value;
  if (!current || busy.value) return;
  if (!reason.value.trim()) {
    error.value = '要写原因：接手的人看到的是原因，不是一个数字。';
    return;
  }
  busy.value = true;
  error.value = '';
  try {
    const saved = await eqsApi.escalateIssue(props.issue.id, { toLevel: picked.value, reason: reason.value.trim() });
    const targets = saved.escalation?.targets ?? [];
    notify(
      'success',
      `已${picked.value > current.currentLevel ? '升级' : '降级'}到「${issueLevelLabel(picked.value)}」`,
      targets.length ? `已通知：${targets.join('、')}（站内 + 邮件）` : '这一级没有人可通知',
    );
    emit('escalated', saved);
    emit('close');
  } catch (caught) {
    error.value = errorMessage(caught);
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <Teleport to="body">
    <div class="escalate-backdrop" @click.self="emit('close')">
      <section class="escalate-panel" role="dialog" aria-modal="true" aria-labelledby="escalate-title">
        <header>
          <div>
            <span>ISSUE ESCALATION</span>
            <h3 id="escalate-title">{{ issue.code || '' }} {{ issue.title }}</h3>
            <p v-if="info">
              现在在「{{ issueLevelLabel(info.currentLevel) }}」层
              <template v-if="info.floorLevel > 1">
                · {{ info.floorReason || '这条问题有层级地板' }}，最低停在「{{ issueLevelLabel(info.floorLevel) }}」
              </template>
              <template v-if="advice">· 建议升级：{{ advice }}</template>
            </p>
          </div>
          <button type="button" aria-label="关闭" @click="emit('close')">×</button>
        </header>
        <p v-if="busy && !info" class="escalate-muted">正在读取各级由谁接…</p>
        <template v-else-if="info">
          <ul class="escalate-levels">
            <li
              v-for="lv in info.levels"
              :key="lv.level"
              :class="{ current: lv.level === info.currentLevel, picked: lv.level === picked, disabled: !selectable(lv.level) }"
            >
              <label>
                <input v-model="picked" type="radio" name="escalate-level" :value="lv.level" :disabled="!selectable(lv.level)">
                <b :class="`level-dot level-${issueLevelTone(lv.level)}`"></b>
                <span class="lv-name">{{ lv.level }} · {{ lv.label }}</span>
                <span class="lv-people">
                  <template v-if="lv.people.length">{{ lv.people.map((p) => p.name).join('、') }}</template>
                  <em v-else>{{ lv.missing || '没配到人' }}</em>
                </span>
                <small v-if="lv.level === info.currentLevel">当前</small>
              </label>
            </li>
          </ul>
          <label class="escalate-reason">
            <span>原因（必填，接手的人看到的就是这句）</span>
            <textarea v-model="reason" rows="3" placeholder="例：工装供应商第二次延期，项目层面已无手段，需要采购与研发负责人介入。"></textarea>
          </label>
          <p v-if="!info.canRaise && !info.canLower" class="escalate-muted">
            你改不了这条问题的层级：能升级的是质量、本项目项目经理、提出者、责任人或管理员。
          </p>
          <details v-if="info.history.length" class="escalate-history">
            <summary>升降履历（{{ info.history.length }} 条）</summary>
            <ul>
              <li v-for="(h, index) in info.history" :key="index">
                <b>{{ formatDate(h.createdAt) }}</b>
                {{ issueLevelLabel(h.fromLevel, true) }} → {{ issueLevelLabel(h.toLevel, true) }}
                <span>· {{ escalationSourceLabel(h.source) }}{{ h.actorName ? ` · ${h.actorName}` : '' }}</span>
                <small v-if="h.reason">{{ h.reason }}</small>
                <small v-if="h.targetNames">通知：{{ h.targetNames }}</small>
              </li>
            </ul>
          </details>
        </template>
        <p v-if="error" class="escalate-error">{{ error }}</p>
        <footer>
          <button type="button" class="button" @click="emit('close')">取消</button>
          <button
            type="button"
            class="button button-primary"
            :disabled="busy || !info || !selectable(picked)"
            @click="submit"
          >{{ info && picked < info.currentLevel ? '确认降级' : '确认升级并通知' }}</button>
        </footer>
      </section>
    </div>
  </Teleport>
</template>

<style scoped>
.escalate-backdrop {
  position: fixed; inset: 0; z-index: 60;
  display: flex; align-items: center; justify-content: center;
  padding: 20px; background: rgba(15, 23, 42, .35);
}
.escalate-panel {
  width: min(680px, 100%); max-height: 90vh; overflow: auto;
  display: grid; gap: 14px; padding: 18px 20px;
  background: var(--white); border: 1px solid var(--line); border-radius: 6px;
}
.escalate-panel > header { display: flex; justify-content: space-between; gap: 12px; align-items: flex-start; }
.escalate-panel > header span { color: var(--blue); font: 800 11px var(--display); letter-spacing: .1em; }
.escalate-panel > header h3 { margin: 4px 0 2px; font-size: 16px; }
.escalate-panel > header p { margin: 0; color: var(--muted); font-size: 12px; }
.escalate-panel > header button { border: 0; background: transparent; font-size: 20px; cursor: pointer; color: var(--muted); }
.escalate-levels { list-style: none; margin: 0; padding: 0; display: grid; gap: 6px; }
.escalate-levels label {
  display: grid; grid-template-columns: auto auto 1fr auto; align-items: center; gap: 10px;
  padding: 8px 10px; border: 1px solid var(--line); border-radius: 4px; cursor: pointer;
}
.escalate-levels li.picked label { border-color: var(--blue); background: rgba(0,117,174,.06); }
.escalate-levels li.current label { background: var(--surface); }
.escalate-levels li.disabled label { cursor: not-allowed; opacity: .6; }
.escalate-levels .lv-name { font-weight: 800; white-space: nowrap; }
.escalate-levels .lv-people { color: var(--muted); font-size: 12px; }
.escalate-levels .lv-people em { color: var(--risk); font-style: normal; }
.escalate-levels small { color: var(--muted); font-size: 11px; }
.level-dot { display: inline-block; width: 10px; height: 10px; border-radius: 50%; border: 1px solid rgba(0,0,0,.15); }
.level-dot.level-grey { background: #cbd5e1; }
.level-dot.level-green { background: #34c26b; }
.level-dot.level-yellow { background: #f5c33b; }
.level-dot.level-red { background: #e5484d; }
.escalate-reason { display: grid; gap: 4px; font-size: 12px; color: var(--muted); }
.escalate-reason textarea { font: inherit; font-size: 13px; color: var(--ink); padding: 6px 8px; border: 1px solid var(--line-strong); border-radius: 4px; }
.escalate-muted { margin: 0; color: var(--muted); font-size: 12px; }
.escalate-error { margin: 0; color: var(--red); font-size: 13px; font-weight: 700; }
.escalate-history summary { cursor: pointer; font-size: 12px; color: var(--muted); }
.escalate-history ul { list-style: none; margin: 6px 0 0; padding: 0; display: grid; gap: 6px; font-size: 12px; }
.escalate-history li { display: grid; gap: 2px; padding: 6px 8px; border-left: 3px solid var(--line-strong); }
.escalate-history li span { color: var(--muted); }
.escalate-history li small { color: var(--muted); }
.escalate-panel > footer { display: flex; justify-content: flex-end; gap: 8px; }
</style>
